import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { resetDatabase, seedDatabase, getDbStats } from "@/services/db";
import MCPChatIcon from "public/chatmcp.svg";
import { Database, Activity, FileText, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { useLog } from "@/contexts/LogContext";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type TabKey = "database" | "logs" | "stats";

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "database", label: "Database", icon: <Database className="h-5 w-5" /> },
  { key: "logs", label: "Activity Logs", icon: <Activity className="h-5 w-5" /> },
  { key: "stats", label: "Statistics", icon: <FileText className="h-5 w-5" /> },
];

export const DbManager = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("database");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const { log, appendLog } = useLog();
  const [stats, setStats] = useState<any>(null);

  const handleReset = async () => {
    try {
      await resetDatabase();
      appendLog("Database reset complete.");
      toast.success("Database reset complete", {
        description: "All tables have been cleared successfully.",
      });
    } catch (error: any) {
      const errorMsg = `Reset failed: ${error?.message ?? error}`;
      appendLog(errorMsg);
      toast.error("Database reset failed", {
        description: error?.message ?? "An unknown error occurred.",
      });
    }
  };

  const handleSeed = async () => {
    try {
      await seedDatabase();
      appendLog("Database seeded with sample data.");
      toast.success("Database seeded", {
        description: "Sample data has been added successfully.",
      });
    } catch (error: any) {
      const errorMsg = `Seed failed: ${error?.message ?? error}`;
      appendLog(errorMsg);
      toast.error("Database seed failed", {
        description: error?.message ?? "An unknown error occurred.",
      });
    }
  };

  const handleShow = async () => {
    try {
      const data = await getDbStats();
      setStats(data);
      appendLog("Statistics loaded.");
      toast.success("Statistics loaded", {
        description: "Database metrics have been retrieved.",
      });
    } catch (error: any) {
      const errorMsg = `Show failed: ${error?.message ?? error}`;
      appendLog(errorMsg);
      toast.error("Failed to load statistics", {
        description: error?.message ?? "An unknown error occurred.",
      });
    }
  };

  useEffect(() => {
    appendLog("Dev DB Manager ready. This operates on the browser IndexedDB (idb://chatmcp-db).");
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case "database":
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-border/70 bg-white/80 p-4 shadow-sm dark:bg-white/5">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Database Operations</h4>
                <span className="text-xs text-red-600"><AlertTriangle className="inline mr-1 h-4 w-4" /> Use with caution! These tools are for development purposes only.</span>
            </div>
              <p className="text-sm text-muted-foreground mb-4">
                Manage your local database. These operations cannot be undone.
              </p>              
              <div className="flex flex-wrap gap-2">
                <Button variant="destructive" onClick={() => setShowResetConfirm(true)}>
                  Reset tables
                </Button>
                <Button variant="secondary" className="hover:bg-muted" onClick={handleSeed}>
                  Seed sample data
                </Button>
              </div>
            </div>
            <div className="rounded-xl border border-border/70 bg-white/80 p-4 shadow-sm dark:bg-white/5">
              <h4 className="text-sm font-semibold text-foreground mb-2">Database Info</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p><span className="font-medium text-foreground">Storage:</span> Browser IndexedDB</p>
                <p><span className="font-medium text-foreground">Database:</span> idb://chatmcp-db</p>
                <p><span className="font-medium text-foreground">Tables:</span> messages, tools, profiles</p>
              </div>
            </div>
          </div>
        );
      case "logs":
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-border/70 bg-white/80 p-4 shadow-sm dark:bg-white/5">
              <h4 className="text-sm font-semibold text-foreground mb-2">Activity Logs</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Recent database operations and events.
              </p>
              <div className="rounded-xl border border-border/60 bg-white/80 p-3 text-xs text-slate-700 shadow-inner max-h-96 overflow-y-auto">
                {log.length === 0 ? (
                  <p className="text-muted-foreground">No log entries yet.</p>
                ) : (
                  log.map((line, idx) => (
                    <div key={idx} className="py-1 border-b border-border/30 last:border-0">
                      {line}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      case "stats":
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-border/70 bg-white/80 p-4 shadow-sm dark:bg-white/5">
              <h4 className="text-sm font-semibold text-foreground mb-2">Database Statistics</h4>
              <p className="text-sm text-muted-foreground mb-1">
                Current database metrics and counts.
              </p>
              <div className="flex flex-wrap gap-2 p-4">
                <Button variant="outline" onClick={handleShow}>
                  Load statistics
                </Button>
              </div>
              {stats ? (
                <div className="grid gap-4 sm:grid-cols-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Messages</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">{stats.messages?.count ?? 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Total: {stats.messages?.total ?? 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Tools</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">{stats.tools?.total ?? 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Total: {stats.tools?.total ?? 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Profiles</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">{stats.profiles?.count ?? 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Total: {stats.profiles?.total ?? 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="rounded-xl border border-border/60 bg-white/80 p-8 text-center shadow-inner">
                  <p className="text-muted-foreground">Click "Load statistics" to view database metrics.</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-10 px-4 py-6 text-slate-900">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <a href="/">
            <img src={MCPChatIcon} alt="ChatMCP Logo" className="h-10 w-10" />
          </a>
          <h1 className="text-2xl font-semibold text-gradient">Dev Admin</h1>
          <div className="space-x-2">
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">Dev only</span>
            <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">Warning</span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/60 bg-white/95 shadow-2xl dark:bg-slate-900">
          {/* Header */}
          <div className="border-b border-border/60 p-2 sm:p-6">
              <p className="text-xs uppercase tracking-wide text-primary">Developer Tools</p>
          </div>

          {/* Main Content Area with Vertical Tabs */}
          <div className="flex">
            {/* Vertical Tabs Sidebar */}
            <div
              className={`flex flex-col border-r border-border/60 bg-white/70 transition-all duration-300 dark:bg-white/5 ${
                isCollapsed ? "w-16" : "w-48"
              }`}
              role="tablist"
              aria-orientation="vertical"
            >
              {/* Collapse/Expand Button */}
              <div className="flex h-12 items-center justify-end border-b border-border/40 px-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  className="h-8 w-8"
                >
                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
              </div>

              {/* Tab Buttons */}
              <div className="flex flex-col gap-1 p-2">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    aria-controls={`panel-${tab.key}`}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-all ${
                      activeTab === tab.key
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground hover:bg-muted"
                    } ${isCollapsed ? "justify-center" : ""}`}
                    title={isCollapsed ? tab.label : undefined}
                  >
                    <span className="flex-shrink-0">{tab.icon}</span>
                    <span
                      className={`whitespace-nowrap transition-all ${
                        isCollapsed ? "w-0 overflow-hidden opacity-0" : "w-auto opacity-100"
                      }`}
                    >
                      {tab.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 p-6">
              <div
                role="tabpanel"
                id={`panel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
                className="min-h-[320px]"
              >
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Reset */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Are you absolutely sure?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete all data from your local database,
              including messages, tools, and profiles. You will need to re-seed the database to use sample data again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="px-3 py-2 rounded-2xl border hover:bg-muted">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowResetConfirm(false);
                handleReset();
              }}
              className="px-3 py-2 rounded-2xl text-white bg-destructive hover:bg-destructive/90"
            >
              Yes, reset database
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Toaster />
    </div>
  );
};
